import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Home, Wrench, LogOut, Plus, Building2, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";
import { cn } from "../lib/utils";
import { LogoMark } from "./LogoMark";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = ["admin", "manager"].includes(user?.role);

  const links = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/requests", label: isAdmin ? "All Requests" : "My Requests", icon: Wrench },
  ];

  const NavLinks = () => (
    <>
      {links.map((l) => {
        const active = loc.pathname === l.to;
        return (
          <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
            data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
            className={cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              active ? "bg-primary text-primary-foreground" : "text-stone-600 hover:bg-secondary hover:text-primary")}>
            <l.icon className="h-4 w-4" aria-hidden="true" />
            {l.label}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card px-4 py-6 fixed h-full">
        <Link to="/dashboard" className="flex items-center gap-2 px-2 mb-8">
          <LogoMark size={38} />
          <div className="leading-tight">
            <div className="font-heading font-bold text-lg tracking-tight">Nustamac</div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground">Walo Platform</div>
          </div>
        </Link>
        <nav className="flex flex-col gap-1">
          <NavLinks />
          {!isAdmin && (
            <Link to="/properties/new" onClick={() => setOpen(false)} data-testid="nav-add-property"
              className="mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-accent-foreground bg-accent hover:bg-accent/90 transition-colors">
              <Plus className="h-4 w-4" aria-hidden="true" /> Register Property
            </Link>
          )}
        </nav>
        <div className="mt-auto pt-4 border-t border-border">
          <div className="px-2 mb-3">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground capitalize">{user?.role}{user?.country ? ` · ${user.country}` : ""}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/login"); }}
            data-testid="logout-button" className="w-full justify-start text-stone-600 hover:text-destructive">
            <LogOut className="h-4 w-4 mr-2" aria-hidden="true" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between border-b border-border bg-card px-4 h-14">
        <Link to="/dashboard" className="flex items-center gap-2">
          <LogoMark size={30} rounded={8} />
          <span className="font-heading font-bold">Nustamac Walo</span>
        </Link>
        <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>
      {open && (
        <div className="md:hidden fixed top-14 inset-x-0 z-40 bg-card border-b border-border px-4 py-4 flex flex-col gap-1">
          <NavLinks />
          {!isAdmin && (
            <Link to="/properties/new" onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-accent-foreground bg-accent">
              <Plus className="h-4 w-4" aria-hidden="true" /> Register Property
            </Link>
          )}
          <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/login"); }} className="justify-start mt-1">
            <LogOut className="h-4 w-4 mr-2" aria-hidden="true" /> Sign out
          </Button>
        </div>
      )}

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 min-w-0 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">{children}</div>
      </main>
    </div>
  );
}
